import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateOrder, triggerAiAnalysis, notifyCs, notifyDevTeam, checkOrderStatus, exportJob, resubmitOrder } from '../api/client';
import type { AiModelKey } from '../utils/aiModels';

export function useUpdateOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      date,
      orderId,
      status,
      notes,
    }: {
      date: string;
      orderId: string;
      status: 'pending' | 'resolved';
      notes?: string;
    }) => updateOrder(date, orderId, status, notes),
    onSuccess: (_, variables) => {
      // Invalidate the batch query to refresh the data
      queryClient.invalidateQueries({ queryKey: ['batch', variables.date] });
      queryClient.invalidateQueries({ queryKey: ['batches'] });
      queryClient.invalidateQueries({ queryKey: ['action-required'] });
    },
  });
}

export function useCheckStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ date, orderId }: { date: string; orderId: string }) =>
      checkOrderStatus(date, orderId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['batch', variables.date] });
      queryClient.invalidateQueries({ queryKey: ['batches'] });
      queryClient.invalidateQueries({ queryKey: ['action-required'] });
    },
  });
}

export function useAiAnalysis() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ date, orderId, aiModel }: { date: string; orderId: string; aiModel?: AiModelKey }) =>
      triggerAiAnalysis(date, orderId, aiModel),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['batch', variables.date] });
      queryClient.invalidateQueries({ queryKey: ['action-required'] });
    },
  });
}

export function useNotifyCs() {
  return useMutation({
    mutationFn: ({ date, orderId }: { date: string; orderId: string }) =>
      notifyCs(date, orderId),
  });
}

export function useNotifyDevTeam() {
  return useMutation({
    mutationFn: ({ date, orderId }: { date: string; orderId: string }) =>
      notifyDevTeam(date, orderId),
  });
}

export function useExportJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ date, orderId }: { date: string; orderId: string }) =>
      exportJob(date, orderId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['batch', variables.date] });
      queryClient.invalidateQueries({ queryKey: ['action-required'] });
    },
  });
}

export function useResubmitOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ date, orderId }: { date: string; orderId: string }) =>
      resubmitOrder(date, orderId),
    onSettled: (_, __, variables) => {
      queryClient.invalidateQueries({ queryKey: ['batch', variables.date] });
      queryClient.invalidateQueries({ queryKey: ['batches'] });
      queryClient.invalidateQueries({ queryKey: ['action-required'] });
    },
  });
}
